/**
 * Object → «Огляд» (iteration 2): above the sections — the connection
 * («Немає зв'язку з 11:05»; D4: «Очікує доступу», «Доступ закрито» with
 * «Перевірити доступ», «Немає права керування») and the history download («завантажуємо: 36 з 90 днів», «мало даних», «помилка» with
 * «Повторити зараз»); then «Зараз», «Енергія», «Гроші» (D11), «Профіль споживання»,
 * «Внутрішній арбітраж», «Зовнішній арбітраж». The administrator opens the
 * same screen with the client's line above it.
 */
import { Link, useParams } from 'react-router-dom';
import { todayInTz } from 'shared';

import { AdminBanner } from '../../components/AdminBanner.js';
import { AppShell } from '../../components/AppShell.js';
import { AccessNote } from '../../components/PhageAccess.js';
import { useNow } from '../../components/charts/day-axis.js';
import { apiStatus, errorText, notFound } from '../../components/errors.js';
import { fmtSince } from '../../components/format.js';
import { HISTORY_DAYS, type HistState, connState, histState } from '../../components/site-state.js';
import { useExpectedLoad } from '../../hooks/use-expected-load.js';
import { useProfile } from '../../hooks/use-profile.js';
import {
  useRestartAnalysis,
  useSiteAnalysis,
  useSiteDay,
  useSiteMoney,
  useSiteOverview,
  useSitePotential,
  useSiteWindows,
} from '../../hooks/use-site-results.js';
import { useSite } from '../../hooks/use-sites.js';
import { useLocale } from '../../i18n/locale-context.js';
import { NotFoundCard } from './NotFoundPage.js';
import { SiteHeader } from './SiteHeader.js';
import { ArbitrageCard } from './site/ArbitrageCard.js';
import { EnergyCard } from './site/EnergyCard.js';
import { MoneyCard } from './site/MoneyCard.js';
import { NowCard } from './site/NowCard.js';
import { PotentialCard } from './site/PotentialCard.js';
import { ProfileCard } from './site/ProfileCard.js';

function HistNote({ hist, siteId, asAdmin }: { hist: HistState | null; siteId: string; asAdmin: boolean }) {
  const { t, tf, tp } = useLocale();
  const restart = useRestartAnalysis(siteId, asAdmin);
  if (!hist || hist.kind === 'done') return null;
  if (hist.kind === 'error') {
    const failed = restart.error
      ? apiStatus(restart.error) === 409
        ? t('site.histnote.no_access')
        : errorText(restart.error, tf)
      : null;
    return (
      <div className="hist-note err" aria-live="polite">
        <span>{restart.isSuccess ? t('site.histnote.queued') : (failed ?? t('site.histnote.error'))}</span>
        {restart.isSuccess ? null : (
          <button type="button" className="btn" onClick={() => restart.mutate()} disabled={restart.isPending}>
            {t('site.histnote.retry')}
          </button>
        )}
      </div>
    );
  }
  if (hist.kind === 'pending') {
    return (
      <div className="hist-note" aria-live="polite">
        <span>{t('site.histnote.queued')}</span>
      </div>
    );
  }
  if (hist.kind === 'few') {
    return (
      <div className="hist-note warn" aria-live="polite">
        <span>{tp('site.histnote.few', hist.days)}</span>
      </div>
    );
  }
  return (
    <div className="hist-note" aria-live="polite">
      <span>{tf('site.histnote.loading', { done: hist.done, total: HISTORY_DAYS })}</span>
    </div>
  );
}

export function SitePage() {
  const { id = '' } = useParams();
  const { t, tf } = useLocale();
  const me = useProfile();
  const isAdmin = me.data?.role === 'operator';
  const site = useSite(id);
  const overview = useSiteOverview(id);
  const analysis = useSiteAnalysis(id);
  const money = useSiteMoney(id);
  const status = analysis.data?.status;
  const potential = useSitePotential(id, status === 'pending' || status === 'running');
  // D4: waiting for Phage.Energy's access — no history download yet, nothing to show.
  const waiting = (overview.data?.site.phage_access ?? site.data?.phage_access.status) === 'pending';
  const hist = waiting ? null : histState(analysis.data, potential.data?.status === 'ok');
  const windows = useSiteWindows(id);
  const profile = useExpectedLoad(id);
  const nowMs = useNow();
  const tz = site.data?.timezone ?? '';
  const today = tz ? todayInTz(tz) : '';
  const isOwner = overview.data?.is_owner ?? false;
  const day = useSiteDay(id, today);

  if (notFound(site.error) || notFound(overview.error)) {
    return (
      <AppShell>
        <div className="view">
          <NotFoundCard />
        </div>
      </AppShell>
    );
  }
  if (!site.data || !overview.data) {
    return (
      <AppShell>
        <p className={site.isError || overview.isError ? 'hist-note err' : 'loading'}>
          {t(site.isError || overview.isError ? 'common.load_error' : 'common.loading')}
        </p>
      </AppShell>
    );
  }

  const s = site.data;
  const o = overview.data.site;
  const latest = overview.data.latest;
  const conn = connState({ access: o.phage_access, vendor_down_since: o.vendor_down_since }, latest, nowMs);

  return (
    <AppShell>
      <div className="view">
        <AdminBanner siteId={id} />
        <SiteHeader
          site={{
            ...s,
            name: o.name,
            phage_access: { ...s.phage_access, status: o.phage_access },
            vendor_down_since: o.vendor_down_since,
          }}
          latest={latest}
          hist={hist}
          tab="overview"
        />
        {conn === 'off' && latest ? (
          <div className="hist-note warn" aria-live="polite">
            <span>{tf('site.connnote.off', { time: fmtSince(latest.ts, tz, today) })}</span>
          </div>
        ) : null}
        {conn === 'down' && o.vendor_down_since ? (
          <div className="hist-note" aria-live="polite">
            <span>{tf('site.connnote.down', { time: fmtSince(o.vendor_down_since, tz, today) })}</span>
          </div>
        ) : null}
        {/* «Only view» is a mode, not a fault (owner, 25.10.2026): the arbitrage card tells about control. */}
        {o.phage_access !== 'open' && o.phage_access !== 'noedit' ? (
          <AccessNote
            site={{ ...s, phage_access: { ...s.phage_access, status: o.phage_access } }}
            state={o.phage_access}
          />
        ) : null}
        {o.phage_access === 'open' && (!s.tariff_type || s.capacity_kwh === null || s.p_max_kw === null) ? (
          <div className="hist-note warn" aria-live="polite">
            <span>{t(s.tariff_type ? 'site.params_missing' : 'site.pick_tariff')}</span>
            <Link className="btn small" to={`/app/sites/${id}/settings`}>
              {t('site.to_settings')}
            </Link>
          </div>
        ) : null}
        <HistNote hist={hist} siteId={id} asAdmin={isAdmin} />
        <NowCard latest={latest} hasPv={s.has_pv} socMinPct={o.soc_min_pct} conn={conn} tz={tz} />
        <EnergyCard analysis={analysis.data} hasPv={s.has_pv} />
        <MoneyCard money={money.data} site={s} />
        <ProfileCard siteId={id} profile={profile.data} today={today} />
        <ArbitrageCard
          site={s}
          overview={o}
          day={day.data}
          windows={windows.data}
          today={today}
          nowMs={nowMs}
          loadKw={profile.data?.hourly_kwh.length === 24 ? profile.data.hourly_kwh : null}
          money={money.data}
        />
        <PotentialCard
          siteId={id}
          potential={potential.data}
          reservePct={o.reserve_pct}
          isOwner={isOwner}
          isAdmin={isAdmin}
          tz={tz}
        />
      </div>
    </AppShell>
  );
}
